import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableHead, TableRow, Alert, IconButton, CircularProgress, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, Divider, Button
} from '@mui/material';
import { Home, Add, AccountBalance } from '@mui/icons-material';
import axios from 'axios';

import { sym } from '../utils/currency';
const STATUS_COLOR = { Draft: 'default', Posted: 'success', Cancelled: 'error' };

const InfoRow = ({ label, value }) => (
  <Box display="flex" gap={1} mb={0.5}>
    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 160, fontWeight: 500 }}>{label}</Typography>
    <Typography variant="body2">{value || <em style={{ color: '#bbb' }}>—</em>}</Typography>
  </Box>
);

const PaymentVoucherList = () => {
  const navigate = useNavigate();
  const { state: navState } = useLocation();
  const [vouchers, setVouchers]   = useState([]);
  const [error, setError]         = useState('');
  const [success, setSuccess]     = useState(navState?.message || '');
  const [loading, setLoading]     = useState(true);
  const [viewVoucher, setViewVoucher] = useState(null);

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  useEffect(() => {
    if (!success) return;
    const t = setTimeout(() => setSuccess(''), 1000);
    return () => clearTimeout(t);
  }, [success]);

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_API_URL}/api/payables/vouchers`, { headers })
      .then(r => setVouchers(r.data))
      .catch(err => setError(err.response?.data?.error || 'Failed to load vouchers'))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line

  if (loading) return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', mt: 4, textAlign: 'center' }}>
      <CircularProgress /><Typography sx={{ mt: 2 }}>Loading vouchers...</Typography>
    </Box>
  );

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', mt: 4 }}>
      <Card elevation={3}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Box display="flex" alignItems="center">
              <AccountBalance sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h5" color="primary">Payment Vouchers</Typography>
            </Box>
            <Box>
              <IconButton onClick={() => navigate('/payables')} title="Payables Home"
                sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' }, mr: 1 }}>
                <Home />
              </IconButton>
              <IconButton onClick={() => navigate('/payables/vouchers/new')} title="New Voucher"
                sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' } }}>
                <Add />
              </IconButton>
            </Box>
          </Box>

          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {vouchers.length === 0 ? (
            <Typography>No payment vouchers yet.</Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Voucher No</strong></TableCell>
                  <TableCell><strong>Invoice No</strong></TableCell>
                  <TableCell><strong>Supplier</strong></TableCell>
                  <TableCell><strong>Date</strong></TableCell>
                  <TableCell><strong>Bank</strong></TableCell>
                  <TableCell><strong>Reference</strong></TableCell>
                  <TableCell align="right"><strong>Amount ({sym()})</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {vouchers.map(v => (
                  <TableRow key={v.voucherNo} hover sx={{ cursor: 'pointer' }}
                    onClick={() => setViewVoucher(v)}>
                    <TableCell><strong>{v.voucherNo}</strong></TableCell>
                    <TableCell>{v.invoiceNo}</TableCell>
                    <TableCell>{v.supplierCode}</TableCell>
                    <TableCell>{v.voucherDate ? new Date(v.voucherDate).toLocaleDateString() : ''}</TableCell>
                    <TableCell>{v.bankAccountNo}</TableCell>
                    <TableCell>{v.reference}</TableCell>
                    <TableCell align="right">{(v.amount || 0).toFixed(2)}</TableCell>
                    <TableCell>
                      <Chip label={v.status}
                        color={STATUS_COLOR[v.status] || 'default'}
                        size="small" variant="outlined" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* ── Voucher Card (view) ── */}
      <Dialog open={Boolean(viewVoucher)} onClose={() => setViewVoucher(null)} maxWidth="sm" fullWidth>
        {viewVoucher && (
          <>
            <DialogTitle component="div">
              <Box display="flex" alignItems="center" gap={1}>
                <AccountBalance color="primary" />
                <Box>
                  <Typography variant="h6" lineHeight={1}>{viewVoucher.voucherNo}</Typography>
                  <Typography variant="caption" color="text.secondary">Payment Voucher</Typography>
                </Box>
                <Box ml="auto">
                  <Chip label={viewVoucher.status}
                    color={STATUS_COLOR[viewVoucher.status] || 'default'}
                    size="small" variant="outlined" />
                </Box>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <Typography variant="subtitle2" color="primary" gutterBottom>Document</Typography>
              <InfoRow label="Voucher No"   value={viewVoucher.voucherNo} />
              <InfoRow label="Invoice No"   value={viewVoucher.invoiceNo} />
              <InfoRow label="Voucher Date" value={viewVoucher.voucherDate ? new Date(viewVoucher.voucherDate).toLocaleDateString() : ''} />

              <Divider sx={{ my: 1.5 }} />

              <Typography variant="subtitle2" color="primary" gutterBottom>Supplier</Typography>
              <InfoRow label="Supplier Code" value={viewVoucher.supplierCode} />

              <Divider sx={{ my: 1.5 }} />

              <Typography variant="subtitle2" color="primary" gutterBottom>Payment Details</Typography>
              <InfoRow label="Bank Account No" value={viewVoucher.bankAccountNo} />
              <InfoRow label="Reference"       value={viewVoucher.reference} />
              <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 160, fontWeight: 500 }}>
                  Amount ({sym()})
                </Typography>
                <Typography variant="h6" fontWeight={700} color="success.dark">
                  {(viewVoucher.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </Typography>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button variant="outlined" onClick={() => setViewVoucher(null)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default PaymentVoucherList;
